import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  CheckCircle as CheckCircleIcon,
  Delete as DeleteIcon,
  LinkOff as LinkOffIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ownerConsoleService } from '../../services/ownerConsoleService';
import {
  AvailabilityPolicy,
  AvailabilitySlot,
  CalendarConnection,
  EnrollmentIntent,
  Instrument,
  OpenHoursBlock,
  StripeCustomerSummary,
  TeacherConfig,
} from '../../types/ownerConsole';

type Section = 'dashboard' | 'site' | 'calendar' | 'schedule' | 'stripe' | 'enrollments';

interface OwnerConsolePageProps {
  section: Section;
}

const stripeDashboardUrl = 'https://dashboard.stripe.com/products';
const dayOptions = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

function money(amount?: number | null, currency?: string): string {
  if (amount === null || amount === undefined) {
    return 'Not set';
  }
  return `$${amount.toFixed(2)} ${currency || ''}`.trim();
}

function QueryState<T>({
  query,
  children,
}: {
  query: { isLoading: boolean; error: unknown; data?: T };
  children: (data: T) => React.ReactNode;
}) {
  if (query.isLoading) {
    return <Typography>Loading...</Typography>;
  }
  if (query.error) {
    return <Alert severity="warning">{query.error instanceof Error ? query.error.message : 'Unable to load data'}</Alert>;
  }
  if (!query.data) {
    return <Alert severity="info">No data available.</Alert>;
  }
  return <>{children(query.data)}</>;
}

function dateKey(value: string): string {
  return new Date(value).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function groupSlotsByDate(slots: AvailabilitySlot[]): Record<string, AvailabilitySlot[]> {
  return slots.reduce<Record<string, AvailabilitySlot[]>>((groups, slot) => {
    const key = dateKey(slot.start);
    groups[key] = groups[key] || [];
    groups[key].push(slot);
    return groups;
  }, {});
}

function slugifyInstrumentName(value: string, fallback: string): string {
  const slug = value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

function statusLabel(status: string): string {
  return status.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function instrumentLabel(instruments: Instrument[] | undefined, instrumentId: string): string {
  const instrument = instruments?.find((item) => item.id === instrumentId);
  return instrument?.display_name || instrument?.name || instrumentId || 'No instrument';
}

function enrollmentName(row: EnrollmentIntent, customer?: StripeCustomerSummary): string {
  return row.student_name || customer?.name || row.contact_email || customer?.email || 'Prospective student';
}

function AvailabilityPolicyEditor({
  policy,
  onSave,
  isSaving,
}: {
  policy: AvailabilityPolicy;
  onSave: (policy: AvailabilityPolicy) => void;
  isSaving: boolean;
}) {
  const [draft, setDraft] = React.useState<AvailabilityPolicy>(policy);

  React.useEffect(() => {
    setDraft(policy);
  }, [policy]);

  const updateField = (field: keyof AvailabilityPolicy, value: string | number) => {
    setDraft((current) => ({ ...current, [field]: value }));
  };

  const updateBlock = (index: number, block: OpenHoursBlock) => {
    setDraft((current) => ({
      ...current,
      open_hours: current.open_hours.map((item, itemIndex) => (itemIndex === index ? block : item)),
    }));
  };

  const addBlock = () => {
    setDraft((current) => ({
      ...current,
      open_hours: [
        ...current.open_hours,
        { days: [1], timezone: current.timezone, start: '15:00', end: '18:00' },
      ],
    }));
  };

  const removeBlock = (index: number) => {
    setDraft((current) => ({
      ...current,
      open_hours: current.open_hours.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Availability Policy</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Timezone"
              value={draft.timezone}
              onChange={(event) => {
                const timezone = event.target.value;
                setDraft((current) => ({
                  ...current,
                  timezone,
                  open_hours: current.open_hours.map((block) => ({ ...block, timezone })),
                }));
              }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Session"
              value={draft.duration_minutes}
              onChange={(event) => updateField('duration_minutes', Number(event.target.value))}
              inputProps={{ min: 15, max: 180, step: 5 }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Interval"
              value={draft.interval_minutes}
              onChange={(event) => updateField('interval_minutes', Number(event.target.value))}
              inputProps={{ min: 5, max: 120, step: 5 }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Buffer"
              value={draft.buffer_minutes}
              onChange={(event) => updateField('buffer_minutes', Number(event.target.value))}
              inputProps={{ min: 0, max: 120, step: 5 }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Min notice"
              value={draft.min_notice_hours}
              onChange={(event) => updateField('min_notice_hours', Number(event.target.value))}
              inputProps={{ min: 0, max: 720 }}
            />
          </Grid>
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              type="number"
              label="Horizon"
              value={draft.booking_horizon_days}
              onChange={(event) => updateField('booking_horizon_days', Number(event.target.value))}
              inputProps={{ min: 1, max: 90 }}
            />
          </Grid>
        </Grid>

        <Stack spacing={1}>
          {draft.open_hours.map((block, index) => (
            <Grid container spacing={1} alignItems="center" key={`${block.days.join('-')}-${index}`}>
              <Grid item xs={12} sm={4}>
                <TextField
                  select
                  fullWidth
                  label="Day"
                  value={block.days[0] ?? 1}
                  onChange={(event) => updateBlock(index, { ...block, days: [Number(event.target.value)] })}
                >
                  {dayOptions.map((day) => (
                    <MenuItem key={day.value} value={day.value}>{day.label}</MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  fullWidth
                  type="time"
                  label="Start"
                  value={block.start}
                  onChange={(event) => updateBlock(index, { ...block, start: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={5} sm={3}>
                <TextField
                  fullWidth
                  type="time"
                  label="End"
                  value={block.end}
                  onChange={(event) => updateBlock(index, { ...block, end: event.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={2}>
                <IconButton aria-label="Remove availability block" onClick={() => removeBlock(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button startIcon={<AddIcon />} onClick={addBlock} variant="outlined">
            Add Block
          </Button>
          <Button variant="contained" onClick={() => onSave(draft)} disabled={isSaving || draft.open_hours.length === 0}>
            Save Availability
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function SiteConfigEditor({
  config,
  onSave,
  isSaving,
}: {
  config: { teacher: TeacherConfig; instruments: Instrument[] };
  onSave: (config: { teacher: TeacherConfig; instruments: Instrument[] }) => void;
  isSaving: boolean;
}) {
  const [draft, setDraft] = React.useState(config);

  React.useEffect(() => {
    setDraft(config);
  }, [config]);

  const updateTeacher = (field: keyof TeacherConfig, value: string) => {
    setDraft((current) => ({ ...current, teacher: { ...current.teacher, [field]: value } }));
  };

  const updateInstrument = (index: number, instrument: Instrument) => {
    setDraft((current) => ({
      ...current,
      instruments: current.instruments.map((item, itemIndex) => (itemIndex === index ? instrument : item)),
    }));
  };

  const addInstrument = () => {
    setDraft((current) => ({
      ...current,
      instruments: [
        ...current.instruments,
        {
          id: `instrument-${current.instruments.length + 1}`,
          name: '',
          display_name: '',
          description: '',
          active: true,
          sort_order: current.instruments.length + 1,
        },
      ],
    }));
  };

  const removeInstrument = (index: number) => {
    setDraft((current) => ({
      ...current,
      instruments: current.instruments.filter((_, itemIndex) => itemIndex !== index),
    }));
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Teacher</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Name" value={draft.teacher.name} onChange={(event) => updateTeacher('name', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Email" value={draft.teacher.email} onChange={(event) => updateTeacher('email', event.target.value)} />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField fullWidth label="Timezone" value={draft.teacher.timezone} onChange={(event) => updateTeacher('timezone', event.target.value)} />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="About"
              value={draft.teacher.about || ''}
              onChange={(event) => updateTeacher('about', event.target.value)}
            />
          </Grid>
        </Grid>

        <Typography variant="h6">Instruments</Typography>
        <Stack spacing={1}>
          {draft.instruments.map((instrument, index) => (
            <Grid container spacing={1} alignItems="center" key={`${instrument.id}-${index}`}>
              <Grid item xs={10} md={11}>
                <TextField
                  fullWidth
                  label="Name"
                  value={instrument.name}
                  onChange={(event) => {
                    const name = event.target.value;
                    updateInstrument(index, {
                      ...instrument,
                      id: slugifyInstrumentName(name, instrument.id || `instrument-${index + 1}`),
                      name,
                      display_name: name,
                      sort_order: index + 1,
                    });
                  }}
                />
              </Grid>
              <Grid item xs={2} md={1}>
                <IconButton aria-label="Remove instrument" onClick={() => removeInstrument(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          ))}
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
          <Button startIcon={<AddIcon />} onClick={addInstrument} variant="outlined">Add Instrument</Button>
          <Button
            variant="contained"
            onClick={() => onSave({
              ...draft,
              instruments: draft.instruments.map((instrument, index) => ({
                ...instrument,
                id: slugifyInstrumentName(instrument.name || instrument.display_name || '', instrument.id || `instrument-${index + 1}`),
                display_name: instrument.name,
                sort_order: index + 1,
              })),
            })}
            disabled={isSaving || !draft.teacher.name || draft.instruments.some((instrument) => !instrument.name)}
          >
            Save Teacher
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ScheduleView() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [mode, setMode] = React.useState<'day' | 'week'>(() => (isMobile ? 'day' : 'week'));
  const today = React.useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const schedule = useQuery({
    queryKey: ['owner-schedule', mode, today.toISOString()],
    queryFn: () => ownerConsoleService.availability({ start: today.toISOString(), days: mode === 'day' ? 1 : 7 }),
    retry: 1,
  });

  React.useEffect(() => {
    setMode(isMobile ? 'day' : 'week');
  }, [isMobile]);

  return (
    <Stack spacing={3}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
        <Typography variant="h4">Schedule</Typography>
        <ToggleButtonGroup
          exclusive
          value={mode}
          onChange={(_, value) => value && setMode(value)}
          size="small"
        >
          <ToggleButton value="day">Day</ToggleButton>
          <ToggleButton value="week">Week</ToggleButton>
        </ToggleButtonGroup>
      </Stack>
      <Paper sx={{ p: 2 }}>
        <QueryState query={schedule}>
          {(slots) => {
            const groups = groupSlotsByDate(slots);
            if (slots.length === 0) {
              return <Alert severity="info">No available sessions in this view.</Alert>;
            }
            return (
              <Stack spacing={2}>
                {Object.entries(groups).map(([date, dateSlots]) => (
                  <Box key={date}>
                    <Typography variant="h6" gutterBottom>{date}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {dateSlots.map((slot) => (
                        <Chip key={slot.availability_id} label={`${new Date(slot.start).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })} (${slot.duration_minutes} min)`} />
                      ))}
                    </Stack>
                  </Box>
                ))}
              </Stack>
            );
          }}
        </QueryState>
      </Paper>
    </Stack>
  );
}

export const OwnerConsolePage: React.FC<OwnerConsolePageProps> = ({ section }) => {
  const queryClient = useQueryClient();
  const [calendarNotice, setCalendarNotice] = React.useState<string | null>(null);
  const [calendarError, setCalendarError] = React.useState<string | null>(null);
  const [isAddStudentOpen, setIsAddStudentOpen] = React.useState(false);
  const [selectedEnrollment, setSelectedEnrollment] = React.useState<EnrollmentIntent | null>(null);
  const [manualEnrollment, setManualEnrollment] = React.useState({
    student_name: '',
    parent_name: '',
    contact_email: '',
    contact_phone: '',
    instrument_id: '',
    status: 'prospective',
    lesson_time: '',
    notes: '',
    stripe_customer_id: '',
    stripe_checkout_session_id: '',
    nylas_event_id: '',
  });
  const site = useQuery({ queryKey: ['owner-site'], queryFn: ownerConsoleService.siteConfig });
  const products = useQuery({ queryKey: ['owner-products'], queryFn: ownerConsoleService.products });
  const prices = useQuery({ queryKey: ['owner-prices'], queryFn: ownerConsoleService.prices });
  const customers = useQuery({ queryKey: ['owner-customers'], queryFn: ownerConsoleService.customers });
  const calendar = useQuery({ queryKey: ['owner-calendar'], queryFn: ownerConsoleService.calendarStatus });
  const availabilityPolicy = useQuery({
    queryKey: ['owner-availability-policy'],
    queryFn: ownerConsoleService.availabilityPolicy,
  });
  const availability = useQuery({
    queryKey: ['owner-availability', availabilityPolicy.data?.duration_minutes],
    queryFn: () => ownerConsoleService.availability({ duration_minutes: availabilityPolicy.data?.duration_minutes }),
    enabled: section === 'site' || section === 'calendar' || section === 'dashboard',
    retry: 1,
  });
  const enrollments = useQuery({
    queryKey: ['owner-enrollments'],
    queryFn: ownerConsoleService.enrollmentIntents,
    enabled: section === 'enrollments' || section === 'dashboard',
  });
  const disconnectCalendar = useMutation({
    mutationFn: ownerConsoleService.disconnectGoogleCalendar,
    onSuccess: () => {
      setCalendarNotice('Google Calendar disconnected.');
      queryClient.invalidateQueries({ queryKey: ['owner-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['owner-availability'] });
    },
    onError: (error) => {
      setCalendarError(error instanceof Error ? error.message : 'Unable to disconnect Google Calendar');
    },
  });
  const saveAvailabilityPolicy = useMutation({
    mutationFn: ownerConsoleService.updateAvailabilityPolicy,
    onSuccess: () => {
      setCalendarNotice('Availability policy saved.');
      queryClient.invalidateQueries({ queryKey: ['owner-availability-policy'] });
      queryClient.invalidateQueries({ queryKey: ['owner-availability'] });
      queryClient.invalidateQueries({ queryKey: ['owner-schedule'] });
    },
    onError: (error) => {
      setCalendarError(error instanceof Error ? error.message : 'Unable to save availability policy');
    },
  });
  const createManualEnrollment = useMutation({
    mutationFn: ownerConsoleService.createManualEnrollment,
    onSuccess: () => {
      setIsAddStudentOpen(false);
      setManualEnrollment({
        student_name: '',
        parent_name: '',
        contact_email: '',
        contact_phone: '',
        instrument_id: '',
        status: 'prospective',
        lesson_time: '',
        notes: '',
        stripe_customer_id: '',
        stripe_checkout_session_id: '',
        nylas_event_id: '',
      });
      queryClient.invalidateQueries({ queryKey: ['owner-enrollments'] });
    },
  });
  const saveSiteConfig = useMutation({
    mutationFn: ownerConsoleService.updateSiteConfig,
    onSuccess: () => {
      setCalendarNotice('Teacher configuration saved.');
      queryClient.invalidateQueries({ queryKey: ['owner-site'] });
    },
    onError: (error) => {
      setCalendarError(error instanceof Error ? error.message : 'Unable to save teacher configuration');
    },
  });

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'CEDAR_GOOGLE_CALENDAR_CONNECTED') {
        return;
      }
      setCalendarError(null);
      setCalendarNotice(`Google Calendar connected${event.data.email ? ` to ${event.data.email}` : ''}.`);
      queryClient.invalidateQueries({ queryKey: ['owner-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['owner-availability'] });
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [queryClient]);

  const openGoogleOAuth = () => {
    setCalendarNotice(null);
    setCalendarError(null);
    const popup = window.open(
      ownerConsoleService.googleAuthorizeUrl(),
      'cedar-google-calendar-oauth',
      'width=560,height=720,menubar=no,toolbar=no,location=yes,status=no'
    );
    if (!popup) {
      setCalendarError('The Google connection window was blocked by the browser.');
      return;
    }
    popup.focus();
  };

  const renderCalendarConnection = (data: CalendarConnection) => (
    <Paper sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
          <Box>
            <Typography variant="h6">Google Calendar</Typography>
            <Typography color="text.secondary">
              {data.connected ? `Public booking is reading availability from ${data.email || 'Google Calendar'}.` : 'Connect the owner calendar used for public booking availability.'}
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            {data.connected ? (
              <>
                <Button variant="contained" color="success" startIcon={<CheckCircleIcon />}>
                  Connected to {data.email || 'Google Calendar'}
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<LinkOffIcon />}
                  onClick={() => disconnectCalendar.mutate()}
                  disabled={disconnectCalendar.isPending}
                >
                  Disconnect
                </Button>
              </>
            ) : (
              <Button variant="contained" onClick={openGoogleOAuth}>
                Connect Google Calendar
              </Button>
            )}
          </Stack>
        </Stack>
        {calendarNotice && <Alert severity="success">{calendarNotice}</Alert>}
        {calendarError && <Alert severity="error">{calendarError}</Alert>}
      </Stack>
    </Paper>
  );

  if (section === 'site') {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">Configuration</Typography>
        <QueryState query={calendar}>
          {(data) => (
            <>
              {renderCalendarConnection(data)}
              <QueryState query={availabilityPolicy}>
                {(policy) => (
                  <AvailabilityPolicyEditor
                    policy={policy}
                    onSave={(nextPolicy) => saveAvailabilityPolicy.mutate(nextPolicy)}
                    isSaving={saveAvailabilityPolicy.isPending}
                  />
                )}
              </QueryState>
              {data.connected && (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>Availability Preview</Typography>
                  <QueryState query={availability}>
                    {(slots) => (
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {slots.slice(0, 8).map((slot) => (
                          <Chip key={slot.availability_id} label={slot.display_label} />
                        ))}
                      </Stack>
                    )}
                  </QueryState>
                </Paper>
              )}
            </>
          )}
        </QueryState>
        <QueryState query={site}>
          {(data) => (
            <SiteConfigEditor
              config={data}
              onSave={(nextConfig) => saveSiteConfig.mutate(nextConfig)}
              isSaving={saveSiteConfig.isPending}
            />
          )}
        </QueryState>
      </Stack>
    );
  }

  if (section === 'calendar') {
    return (
      <Stack spacing={3}>
        <Typography variant="h4">Google Calendar</Typography>
        <QueryState query={calendar}>
          {(data) => renderCalendarConnection(data)}
        </QueryState>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>Availability Preview</Typography>
          <QueryState query={availability}>
            {(slots) => (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {slots.slice(0, 8).map((slot) => (
                  <Chip key={slot.availability_id} label={slot.display_label} />
                ))}
              </Stack>
            )}
          </QueryState>
        </Paper>
      </Stack>
    );
  }

  if (section === 'schedule') {
    return <ScheduleView />;
  }

  if (section === 'stripe') {
    return (
      <Stack spacing={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h4">Stripe</Typography>
          <Button endIcon={<OpenInNewIcon />} href={stripeDashboardUrl} target="_blank" rel="noreferrer">
            Open Stripe
          </Button>
        </Stack>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Products</Typography>
          <QueryState query={products}>
            {(rows) => (
              <Table size="small">
                <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Status</TableCell><TableCell>Product ID</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}><TableCell>{row.name}</TableCell><TableCell>{row.active ? 'Active' : 'Inactive'}</TableCell><TableCell>{row.id}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </QueryState>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Prices</Typography>
          <QueryState query={prices}>
            {(rows) => (
              <Table size="small">
                <TableHead><TableRow><TableCell>Price</TableCell><TableCell>Amount</TableCell><TableCell>Product</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}><TableCell>{row.nickname || row.id}</TableCell><TableCell>{money(row.unit_amount_decimal, row.currency)}</TableCell><TableCell>{row.product}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </QueryState>
        </Paper>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6">Customers</Typography>
          <QueryState query={customers}>
            {(rows) => (
              <Table size="small">
                <TableHead><TableRow><TableCell>Name</TableCell><TableCell>Email</TableCell><TableCell>Customer ID</TableCell></TableRow></TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}><TableCell>{row.name || 'Unnamed'}</TableCell><TableCell>{row.email || 'No email'}</TableCell><TableCell>{row.id}</TableCell></TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </QueryState>
        </Paper>
      </Stack>
    );
  }

  if (section === 'enrollments') {
    const customerById = new Map((customers.data || []).map((customer) => [customer.id, customer]));
    const selectedCustomer = selectedEnrollment?.stripe_customer_id ? customerById.get(selectedEnrollment.stripe_customer_id) : undefined;

    return (
      <Stack spacing={3}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} spacing={2}>
          <Typography variant="h4">Students</Typography>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setIsAddStudentOpen(true)}>
            Add Student
          </Button>
        </Stack>
        <Paper sx={{ p: 2 }}>
          <QueryState query={enrollments}>
            {(rows) => (
              rows.length === 0 ? (
                <Alert severity="info">No students yet.</Alert>
              ) : (
                <Grid container spacing={2}>
                  {rows.map((row) => {
                    const customer = row.stripe_customer_id ? customerById.get(row.stripe_customer_id) : undefined;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={row.id}>
                        <Card variant="outlined" sx={{ height: '100%' }}>
                          <CardActionArea sx={{ height: '100%' }} onClick={() => setSelectedEnrollment(row)}>
                            <CardContent>
                              <Stack spacing={1}>
                                <Typography variant="h6">{enrollmentName(row, customer)}</Typography>
                                <Typography color="text.secondary">{instrumentLabel(site.data?.instruments, row.instrument_id)}</Typography>
                                <Box>
                                  <Chip size="small" label={statusLabel(row.status)} />
                                </Box>
                              </Stack>
                            </CardContent>
                          </CardActionArea>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )
            )}
          </QueryState>
        </Paper>
        <Dialog open={isAddStudentOpen} onClose={() => setIsAddStudentOpen(false)} fullWidth maxWidth="md">
          <DialogTitle>Add Student</DialogTitle>
          <DialogContent sx={{ overflowX: 'hidden' }}>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Box
                sx={{
                  display: 'grid',
                  gap: 2,
                  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
                  '& .MuiFormControl-root': { minWidth: 0 },
                }}
              >
                  <TextField
                    fullWidth
                    label="Student name"
                    value={manualEnrollment.student_name}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, student_name: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Parent name"
                    value={manualEnrollment.parent_name}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, parent_name: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Email"
                    value={manualEnrollment.contact_email}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, contact_email: event.target.value }))}
                  />
                  <TextField
                    fullWidth
                    label="Phone"
                    value={manualEnrollment.contact_phone}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, contact_phone: event.target.value }))}
                  />
                  <TextField
                    select
                    fullWidth
                    label="Instrument"
                    value={manualEnrollment.instrument_id}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, instrument_id: event.target.value }))}
                  >
                    <MenuItem value="">Select instrument</MenuItem>
                    {site.data?.instruments.map((instrument) => (
                      <MenuItem key={instrument.id} value={instrument.id}>{instrument.display_name || instrument.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    select
                    fullWidth
                    label="Status"
                    value={manualEnrollment.status}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, status: event.target.value }))}
                  >
                    <MenuItem value="prospective">Prospective</MenuItem>
                    <MenuItem value="enrolled">Enrolled</MenuItem>
                    <MenuItem value="paused">Paused</MenuItem>
                    <MenuItem value="inactive">Inactive</MenuItem>
                  </TextField>
                  <TextField
                    fullWidth
                    type="datetime-local"
                    label="Lesson time"
                    value={manualEnrollment.lesson_time}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, lesson_time: event.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ step: 300 }}
                    sx={{ gridColumn: '1 / -1' }}
                  />
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    label="Notes"
                    value={manualEnrollment.notes}
                    onChange={(event) => setManualEnrollment((current) => ({ ...current, notes: event.target.value }))}
                    sx={{ gridColumn: '1 / -1' }}
                  />
              </Box>
              {createManualEnrollment.error && (
                <Alert severity="error">
                  {createManualEnrollment.error instanceof Error ? createManualEnrollment.error.message : 'Unable to create manual enrollment'}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsAddStudentOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => createManualEnrollment.mutate({
                student_name: manualEnrollment.student_name || null,
                parent_name: manualEnrollment.parent_name || null,
                contact_email: manualEnrollment.contact_email || null,
                contact_phone: manualEnrollment.contact_phone || null,
                instrument_id: manualEnrollment.instrument_id,
                status: manualEnrollment.status || 'prospective',
                lesson_time: manualEnrollment.lesson_time || null,
                notes: manualEnrollment.notes || null,
              })}
              disabled={!manualEnrollment.student_name || !manualEnrollment.instrument_id || createManualEnrollment.isPending}
            >
              Add Student
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog open={Boolean(selectedEnrollment)} onClose={() => setSelectedEnrollment(null)} fullWidth maxWidth="sm">
          {selectedEnrollment && (
            <>
              <DialogTitle>{enrollmentName(selectedEnrollment, selectedCustomer)}</DialogTitle>
              <DialogContent>
                <Stack spacing={2} sx={{ pt: 1 }}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Instrument</Typography>
                    <Typography>{instrumentLabel(site.data?.instruments, selectedEnrollment.instrument_id)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                    <Typography>{statusLabel(selectedEnrollment.status)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Parent</Typography>
                    <Typography>{selectedEnrollment.parent_name || 'Not set'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Contact</Typography>
                    <Typography>{selectedEnrollment.contact_email || selectedCustomer?.email || 'No email'}</Typography>
                    <Typography>{selectedEnrollment.contact_phone || selectedCustomer?.phone || 'No phone'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Lesson Time</Typography>
                    <Typography>{selectedEnrollment.lesson_time || 'Not set'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                    <Typography whiteSpace="pre-wrap">{selectedEnrollment.notes || 'No notes'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Billing</Typography>
                    <Typography>{selectedEnrollment.stripe_customer_id ? `Stripe customer ${selectedEnrollment.stripe_customer_id}` : 'No Stripe customer yet'}</Typography>
                    <Typography>{selectedEnrollment.stripe_checkout_session_id ? `Checkout ${selectedEnrollment.stripe_checkout_session_id}` : 'No checkout session yet'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">Calendar</Typography>
                    <Typography>{selectedEnrollment.nylas_event_id ? `Calendar event ${selectedEnrollment.nylas_event_id}` : 'No calendar event linked'}</Typography>
                  </Box>
                </Stack>
              </DialogContent>
            </>
          )}
        </Dialog>
      </Stack>
    );
  }

  return (
    <Stack spacing={3}>
      <Typography variant="h4">Owner Dashboard</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Calendar</Typography>
            <QueryState query={calendar}>
              {(data) => <Typography color="text.secondary">{data.connected ? data.email || 'Connected' : 'Not connected'}</Typography>}
            </QueryState>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Stripe Products</Typography>
            <QueryState query={products}>
              {(rows) => <Typography color="text.secondary">{rows.length} products</Typography>}
            </QueryState>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6">Students</Typography>
            <QueryState query={enrollments}>
              {(rows) => <Typography color="text.secondary">{rows.length} student records</Typography>}
            </QueryState>
          </Paper>
        </Grid>
      </Grid>
      <Typography color="text.secondary">
        Public enrollment uses Stripe Checkout and Google Calendar availability through Cedar API adapters.
      </Typography>
      <Link href="/site">Review calendar connection</Link>
    </Stack>
  );
};

export default OwnerConsolePage;
